import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { Link } from 'react-router-dom';

import {
    Typography, Button, makeStyles, useTheme, CircularProgress,
} from '@material-ui/core';

import * as UserStore from '../../../app/store/UserStore';

import urls from '../../../app/urls';
import apiUrls from '../../../youwell-common/apiUrls';
import { HtmlContent, Image } from '../../../youwell-common/components';
import { activityLabels } from '../../../common/constants';
import { useYouwellSelector } from '../../../app/store';
import useYouwellTranslate from '../../../app/localization/translate';
import { CheckMarkIcon } from '../../../common/icons';
import { getElementActionText, moduleBoxStyles } from './ModuleBoxWithOverlay';

const progressSize = 38;
const imageHeight = 180;
const defaultDescriptionHeight = 300;

const styles = theme => ({
    ...moduleBoxStyles(theme),
    root: {
        borderRadius: theme.youwell.shapes.borderRadius,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.youwell.shadows.default,
    },
    imageWrapper: {
        height: imageHeight,
        textAlign: 'center',
    },
    image: {
        maxWidth: '100%',
        maxheight: '100%',
    },
    colorCard: {
        height: imageHeight,
    },
    card: {
        backgroundColor: theme.palette.common.white,
        height: defaultDescriptionHeight,
        padding: theme.spacing(2),

        [theme.breakpoints.down('xs')]: {
            height: 'auto',
        },
    },
    actions: {
        flex: '1 1 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
    },
});


const useStyles = makeStyles(styles, { name: 'ModuleBoxFlat' });

const ModuleBoxFlat = ({
    element, index, className, isCurrent, programId, descriptionHeight,
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const theme = useTheme();
    const translate = useYouwellTranslate();
    const patientId = useYouwellSelector(state => state.auth.profileData.id);
    const isActiveProgram = useYouwellSelector(state => state.programStore.currentProgram?.isActiveProgram);

    const clickElement = label => (e) => {
        if (element.disabled) {
            e.preventDefault();
        } else {
            dispatch(UserStore.actionCreators.sendActivity({
                pid: patientId,
                ppid: programId,
                l: label,
                start: new Date(),
            }));
        }
    }

    if (!element || !element.module) {
        return null;
    }

    const isCompletedElement = isActiveProgram && element.completed;
    const isCurrentElement = isActiveProgram && isCurrent;

    const module = element.module;
    const cardColors = [theme.youwell.colors.grayDark, theme.youwell.colors.grayMiddle, theme.youwell.colors.grayLight];

    const bottomColor = (isCurrentElement || isCompletedElement) ? theme.youwell.colors.primary : theme.youwell.colors.defaultBackground;
    const heightFromConfig = descriptionHeight?.length > 0 ? Number(descriptionHeight) : null;

    return (
        <div className={classnames(classes.root, className)}>
            <div className={classes.imageWrapper}>
                {module.moduleImageId ? (
                    <Image
                        alt={element.name || translate('dashboard.module', { index: index + 1 })}
                        src={`${apiUrls.baseUrl}/${apiUrls.media.file}/${module.moduleImageId}`}
                        className={classes.image}
                    />
                ) : (
                    <div className={classes.colorCard} style={{ backgroundColor: cardColors[index % cardColors.length] }} />
                )}
            </div>
            <div className={classnames(classes.card)} style={{ height: heightFromConfig }}>
                <Typography variant="subtitle2" className={classes.moduleTitle}>
                    {element.name || (<Translate id="dashboard.module" data={{ index: index + 1 }} />)}
                </Typography>

                <HtmlContent html={module.description} className={classes.description} />
            </div>

            <div className={classes.actions} style={{ backgroundColor: bottomColor }}>
                <Button
                    variant="outlined"
                    color="primary"
                    component={Link}
                    to={`${urls.inside.program}/${element.elementGroup}/${index}/${urls.inside.parts.resumeLatest}`}
                    className={classnames(classes.startButton, { [classes.completed]: isCompletedElement, [classes.active]: isCurrentElement })}
                    size="small"
                    disabled={element.disabled}
                    aria-label={element.disabled ? translate('dashboard.unavailableModuleAria') : null}
                    onClick={clickElement(`${activityLabels.dashboard.module}/${index}`)}
                >
                    {isActiveProgram && (
                        <>
                            {isCompletedElement ? (
                                <>
                                    <CheckMarkIcon className={classes.checkIcon} />
                                    <CircularProgress size={progressSize} className={classnames(classes.circleProgress, classes.completeProgress)} variant="determinate" value={100} />
                                </>

                            ) : isCurrentElement && (
                                <CircularProgress size={progressSize} thickness={5} className={classes.circleProgress} variant="determinate" value={element.progress * 100} />
                            )}
                        </>
                    )}

                    <span className={isCompletedElement || isCurrentElement ? classes.shiftedModuleName : null}>
                        {getElementActionText(element, translate, isCurrentElement, isActiveProgram)}
                    </span>
                </Button>
            </div>
        </div>
    );
}

ModuleBoxFlat.propTypes = {
    programId: PropTypes.string.isRequired,
    element: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    isCurrent: PropTypes.bool,
    className: PropTypes.string,
    descriptionHeight: PropTypes.string,
};

ModuleBoxFlat.defaultProps = {
    className: null,
    isCurrent: false,
    descriptionHeight: null,
};

export default ModuleBoxFlat;
